import { ofType } from 'redux-observable';
import { mergeMap, map, filter } from 'rxjs';
import { AppEpic, AppState } from 'src/store';
import { of } from 'rxjs';
import { updateGroupBy, updateTopMemberSelection } from 'src/components/Subheader/Subheader.slice';
import {
  fetchSummaryViewData,
  cleanUp,
  receiveTenantConfig,
} from './SummaryView.slice';
import {
  isScopeDefined,
  isSubheaderLoaded,
  inputIsNotNullOrUndefined,
  topMemInWorklistSelected,
  isSameComponentType,
  maybeGetComponentProps,
} from 'src/utils/Functions/epicsFunctions';
import { isEmpty, isNil } from 'lodash';
import { ConfDefnComponentType, SummaryViewComponent } from 'configuration-schemas/confdefnComponents';
import { updateSelectedItem } from 'src/pages/Worklist/Worklist.slice';
import { isListDataApi } from 'configuration-schemas/confdefnView';
import { getAggBys, ExtraPivotOptions, organizeListDataOptions } from '../StyleColorReview.slice';
import {
  receiveScope,
  receiveScopeConfig,
  receiveScopeRefreshTrigger,
} from 'src/components/AssortmentScopeSelector/AssortmentScopeSelector.slice';
import { receiveFilterStateAfterSubmission } from 'src/components/FilterPanel/FilterPanel.slice';
import { setActivePage, setActiveSubPage } from 'src/pages/NavigationShell/NavigationShell.slice';
import { LENS_ASST_PLAN_REFRESHING_PAGE } from 'src/pages/AssortmentBuild/Planning.types';
import { fetchFloorsetData, receiveFloorsetData } from 'src/components/ConfigurableGrid/ConfigurableGrid.slice';

const filterFloorsetApi = (floorsetApi: SummaryViewComponent['componentProps']['floorsetApi'], state: AppState) =>
  isNil(floorsetApi) || !isEmpty(state.subheader.topMemberOptions);

export const summaryViewLoad: AppEpic = (action$, state$) => {
  return action$.pipe(
    ofType(
      receiveScopeRefreshTrigger.type,
      receiveFilterStateAfterSubmission.type,
      setActivePage.type,
      setActiveSubPage.type,
      receiveScopeConfig.type,
      updateSelectedItem.type,
      updateGroupBy.type,
      updateTopMemberSelection.type,
      receiveFloorsetData.type,
      receiveTenantConfig.type,
      LENS_ASST_PLAN_REFRESHING_PAGE,
    ),
    map(() => {
      const props = maybeGetComponentProps<SummaryViewComponent>(state$.value, ConfDefnComponentType.summaryView);
      return props;
    }),
    filter(inputIsNotNullOrUndefined),
    filter(() => topMemInWorklistSelected(state$.value)),
    filter(() => isScopeDefined(state$.value.scope) && isSubheaderLoaded(state$.value.subheader)),
    filter(({ floorsetApi }) => filterFloorsetApi(floorsetApi, state$.value)),
    mergeMap(({ defns, topMembers: worklistTopMembers, floorsetApi }) => {
      const { dataApi, model: modelDefn } = defns;
      const finalModelDefn = isListDataApi(dataApi) ? dataApi.defnId : modelDefn;

      const aggBy = getAggBys(state$.value.subheader, dataApi);
      const { topMemberOptions, topMemberSelection: floorsetSelection } = state$.value.subheader;
      const maybeFloorsetTopMember = floorsetApi ? floorsetSelection ?? topMemberOptions?.[0]?.id : undefined;

      const topMembers = [worklistTopMembers, maybeFloorsetTopMember]
        .filter((v) => !isNil(v) && v !== '')
        .join(',');

      const options: ExtraPivotOptions = topMembers !== '' ? { topMembers, aggBy } : { aggBy };
      const finalOptions = organizeListDataOptions(options, dataApi);

      if (isSameComponentType<SummaryViewComponent>(state$.value, ConfDefnComponentType.summaryView)) {
        return of(cleanUp(), fetchSummaryViewData(finalModelDefn, finalOptions));
      }

      return of(fetchSummaryViewData(finalModelDefn, finalOptions));
    }),
  );
};

export const fetchSummaryViewFloorsetsOnLoad: AppEpic = (action$, state$) => {
  return action$.pipe(
    ofType(
      receiveScope.type,
      receiveScopeRefreshTrigger.type,
      receiveFilterStateAfterSubmission.type,
      receiveTenantConfig.type,
    ),
    map(() => maybeGetComponentProps<SummaryViewComponent>(state$.value, ConfDefnComponentType.summaryView)),
    filter(inputIsNotNullOrUndefined),
    filter((props) => Boolean(props.floorsetApi)),
    filter(() => topMemInWorklistSelected(state$.value)),
    filter(() => isScopeDefined(state$.value.scope)),
    mergeMap(({ floorsetApi }) => of(fetchFloorsetData(floorsetApi))),
  );
};
